import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import Card from '@mui/material/Card';
import Grid from '@mui/system/Unstable_Grid';
import { Stack, Button } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { ICandidateDocument } from 'src/services/candidates/candidates.types';
import {
  useGetCandidateDocumentsQuery,
  useGetCandidateApplicationsQuery,
  useUpdateCandidateDocumentMutation,
  useRemoveCandidateDocumentMutation,
  useGetCandidateDocumentDownloadUrlMutation,
} from 'src/services/candidates/candidates.service';

import { useTable } from 'src/components/table';
import FileManagerTable from 'src/components/documents/file-manager-table';
import RenameDocumentDialog from 'src/components/documents/rename-document-dialog';

import CandidateDocumentJobAdder from '../../modal/candidate-document-job-adder';
import CandidateDocumentUploadModal from '../../modal/candidate-document-uploader';

const DEFAULT_PAGE = {
  page: 1,
  per_page: 10,
};

type Props = {
  candidateId?: string;
  uploadVisible?: boolean;
};

export default function CandidateDocumentsView({ candidateId, uploadVisible = true }: Props) {
  const [page, setPage] = useState(DEFAULT_PAGE);

  const showUploadModal = useBoolean();

  const { t } = useTranslate();

  const [removeDocument, { isLoading: isRemoving }] = useRemoveCandidateDocumentMutation();

  const [updateDocument, { isLoading: isRenaming }] = useUpdateCandidateDocumentMutation();

  const [getDownloadUrl] = useGetCandidateDocumentDownloadUrlMutation();

  const [renaming, setRenaming] = useState<ICandidateDocument | null>(null);

  const { currentData: candidateDocuments, isFetching } = useGetCandidateDocumentsQuery(
    {
      candidate_id: candidateId as string,
      params: page,
    },
    { skip: !candidateId }
  );

  // "Add to job" is only offered when the candidate is on at least one job -- the same list as
  // the candidate's Jobs tab.
  const { data: applicationsData } = useGetCandidateApplicationsQuery(
    { candidateId: candidateId as string, page: 1, per_page: 50 },
    { skip: !candidateId }
  );
  const applications = applicationsData?.results ?? [];
  const [addToJobDocId, setAddToJobDocId] = useState<string | null>(null);

  const table = useTable();

  const notFound = !candidateDocuments?.count && !isFetching;

  const onAddSuccess = () => {
    showUploadModal.onFalse();
  };

  const handleDeleteItem = async (id: string) => {
    try {
      await removeDocument({
        candidate_doc_id: id,
        candidate_id: candidateId as string
      }).unwrap();

      enqueueSnackbar(t('candidates.api.delete_document.success'), { variant: 'success' });

    } catch (error) {
      enqueueSnackbar(t('candidates.api.delete_document.default_error'), { variant: 'error' });
    }
  };

  /** The storage URL saves under the stored object's name, so the server signs a link carrying the title. */
  const handleDownload = async (id: string) => {
    try {
      const { url } = await getDownloadUrl({
        candidate_id: candidateId as string,
        candidate_doc_id: id,
      }).unwrap();

      window.location.assign(url);
    } catch (error) {
      enqueueSnackbar(t('profile.documents.download_error'), { variant: 'error' });
    }
  };

  const handleRename = async (title: string | null) => {
    if (!renaming) return;

    try {
      await updateDocument({
        candidate_id: candidateId as string,
        candidate_doc_id: renaming.id,
        data: { name: title },
      }).unwrap();

      setRenaming(null);
      enqueueSnackbar(t('profile.documents.rename_modal.success'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('profile.documents.rename_modal.error'), { variant: 'error' });
    }
  };

  return (
    <Grid container spacing={3}>
      {
        uploadVisible && (
          <Grid xs={12}>
            <Stack direction="row" justifyContent="flex-end" mt={2}>
              <Button variant="contained" disabled={!candidateId} onClick={showUploadModal.onTrue}>
                {t('profile.documents.upload')}
              </Button>
            </Stack>
          </Grid>
        )
      }
      <Grid xs={12}>
        <Card>
          <FileManagerTable
            table={table}
            isLoading={isFetching}
            dataFiltered={candidateDocuments?.results ?? []}
            count={candidateDocuments?.count ?? 0}
            onDeleteRow={handleDeleteItem}
            onRenameRow={(id) => setRenaming(candidateDocuments?.results.find((doc) => doc.id === id) ?? null)}
            onDownloadRow={handleDownload}
            onAddToJobRow={applications.length ? setAddToJobDocId : undefined}
            notFound={notFound}
            documentType="company_profile"
            onChangePage={(_e, pageNumber) => setPage({ ...page, page: pageNumber + 1 })}
            onChangePerPage={(e) => setPage({ ...page, per_page: Number(e.target.value) })}
            pageOptions={page}
          />
        </Card>
      </Grid>

      {
        candidateId && (
          <CandidateDocumentUploadModal
            open={showUploadModal.value}
            onClose={showUploadModal.onFalse}
            candidateId={candidateId}
            onUpload={onAddSuccess}
          />
        )
      }

      {candidateId && addToJobDocId && (
        <CandidateDocumentJobAdder
          key={addToJobDocId}
          open
          onClose={() => setAddToJobDocId(null)}
          candidateId={candidateId}
          candidateDocumentId={addToJobDocId}
          applications={applications}
        />
      )}

      <RenameDocumentDialog
        open={Boolean(renaming)}
        onClose={() => setRenaming(null)}
        currentTitle={renaming?.display_name ?? ''}
        originalTitle={renaming?.document.original_name ?? renaming?.document.name ?? ''}
        loading={isRenaming}
        onSubmit={handleRename}
      />
    </Grid>
  );
}
